<template>
  <div class="needs-border-bottom">
    <h2 class="f-24 sz-primary-grey--text font-weight-medium mb-0 border-bottom-grey">
      {{ $t('family.otherNeeds') }}
    </h2>
    <OtherNeedsBox
      v-for="(item, index) in listMap"
      :key="index"
      :title="item.title"
      :special="getSpecial(index, item.hasSpecial, item.special)"
      :list="getList(index, item.list, item.otherList)"
      :other="getCustom(index, item.custom)"
      :icon="item.icon"
      :special-requirements="item.specialRequirements"
      :show-special-on-empty-list="item.showSpecialOnEmptyList ?? false"
      class="d-print-block print-break-avoid"
    />
    <div class="hidden-screen-only mt-2 print-break-avoid">
      <p class="mb-0 font-weight-medium f-16 sz-primary-grey--text">
        {{ $t('family.printInfo.reason') }}
      </p>
      <p class="mb-0 f-16 sz-primary-grey--text">
        {{ $t('family.printInfo.reasonText') }}
      </p>
    </div>
  </div>
</template>

<script>
import OtherNeedsBox from '@/components/Family/OtherNeedsBox';
import has from 'lodash/has';
export default {
    name: 'OtherNeeds',
    components: { OtherNeedsBox },
    props: {
        formattedNeeds: {
            required: true
        }
    },
    data () {
        return {
            listMap: {
                food: {
                    title: this.$t('family.otherNeedsList.food'),
                    hasSpecial: 'hasDietaryRequirements',
                    special: 'dietaryRequirements',
                    list: 'chosenProducts',
                    otherList: ['otherProducts', 'productsForBabies'],
                    specialRequirements: true
                },
                cleaningProducts: {
                    title: this.$t('family.otherNeedsList.cleaningProducts'),
                    hasSpecial: 'hasSpecialRequirements',
                    special: 'specialRequirements',
                    list: 'chosenProducts',
                    otherList: ['otherProducts', 'childrenProducts', 'whatLiquidCleaningProducts'],
                    specialRequirements: true
                },
                clothing: {
                    title: this.$t('family.otherNeedsList.clothing'),
                    hasSpecial: null,
                    special: null,
                    list: 'chosenClothesDetails',
                    otherList: null,
                    specialRequirements: true
                },
                _clothing: {
                    title: this.$t('family.otherNeedsList._clothing'),
                    hasSpecial: null,
                    special: null,
                    list: 'chosenFootwearDetails',
                    otherList: null,
                    specialRequirements: true
                },
                schoolSupplies: {
                    title: this.$t('family.otherNeedsList.schoolSupplies'),
                    hasSpecial: null,
                    special: null,
                    list: 'selectedSupplies',
                    otherList: 'anotherSchoolSupplies',
                    specialRequirements: false
                },
                apartmentEquipment: {
                    title: this.$t('family.otherNeedsList.apartmentEquipment'),
                    hasSpecial: null,
                    special: 'notes',
                    list: 'selectedEquipment',
                    otherList: 'anotherEquipment',
                    specialRequirements: false
                },
                householdAppliances: {
                    title: this.$t('family.otherNeedsList.householdAppliances'),
                    hasSpecial: null,
                    special: 'notes',
                    list: 'selectedAppliances',
                    otherList: 'anotherAppliances',
                    specialRequirements: false
                },
                _householdAppliances: {
                    title: this.$t('family.otherNeedsList.vitalElectronicAppliances'),
                    hasSpecial: null,
                    special: 'vitalElectronicAppliancesNotes',
                    list: 'vitalElectronicAppliances',
                    otherList: null,
                    specialRequirements: false
                },
                otherMaterialNeeds: {
                    title: this.$t('family.otherNeedsList.otherMaterialNeeds'),
                    hasSpecial: null,
                    special: 'anotherMaterialNeedsNotes',
                    custom: { fuelType: true, fuelAmount: true },
                    list: 'selectedMaterialNeeds',
                    otherList: 'anotherMaterialNeeds',
                    specialRequirements: false
                },
                intangibleNeeds: {
                    title: this.$t('family.otherNeedsList.intangibleNeeds'),
                    hasSpecial: null,
                    special: null,
                    list: 'intangibleNeedsList',
                    otherList: null,
                    specialRequirements: false
                },
                specialGifts: {
                    icon: '$fullheart',
                    title: this.$t('family.otherNeedsList.specialGifts'),
                    hasSpecial: null,
                    special: 'giftsNotes',
                    list: 'members',
                    otherList: null,
                    specialRequirements: false,
                    showSpecialOnEmptyList: true
                }
            }
        };
    },
    methods: {
        getSpecial (index, hasSpecial, special) {
            if (index.indexOf('_') >= 0) {
                index = index.substring(1);
            }
            if (has(this.formattedNeeds, index)) {
                if ((has(this.formattedNeeds[index], hasSpecial) &&
                    this.formattedNeeds[index][hasSpecial] === 'yes') || hasSpecial === null) {
                    if (has(this.formattedNeeds[index], special)) {
                        return this.formattedNeeds[index][special];
                    }
                }
            }
            return null;
        },
        getList (index, list, other) {
            if (index.indexOf('_') >= 0) {
                index = index.substring(1);
            }
            if (this.formattedNeeds?.[index]?.[list]) {
                if (other) {
                    return this.mapOtherToList(this.formattedNeeds[index][list], this.getOther(index, other));
                }
                return this.formattedNeeds[index][list];
            }
            return [];
        },
        /**
         * @param {number} index - The index or key used to look up values in the formattedNeeds property.
         * @param {Array|string|object} other - Specifies additional keys for deeper access or an array of keys
         *                                       for multiple values. Can also be an object containing nested keys.
         * --------------------------------------
         * @returns {Array|string|null}
         * - When 'other' is an array, returns an array of strings containing headlines and answers,
         *   filtered to exclude null or empty values.
         * - When 'other' is an object, returns an array containing values that are found using the object keys.
         * - When 'other' is a string or number, returns the corresponding value from formattedNeeds, if exists.
         * - Returns null if no matching data is found.
         */
        getOther (index, other) {
            if (Array.isArray(other)) {
                return other.map(otherElement => {
                    const otherNeedsHeadline = this.$t(`family.otherNeedsHeadlines.${otherElement}`);
                    const otherNeedAnswer = this.formattedNeeds?.[index]?.[otherElement];
                    if (!otherNeedAnswer) return null;
                    return `${otherNeedsHeadline} (${otherNeedAnswer})`;
                }).filter(otherElement => !!otherElement);
            }
            if (typeof other === 'object') {
                const items = [];
                for (const item in other) {
                    if (this.formattedNeeds?.[index]?.[other]?.[item]) {
                        items.push(this.formattedNeeds[index][other][item]);
                    }
                }
                return items;
            } else if (this.formattedNeeds?.[index]?.[other]) {
                return this.formattedNeeds[index][other];
            }
            return null;
        },
        mapOtherToList (list, other) {
            if (!other) return list;
            if (typeof other === 'string') {
                const index = list.indexOf('Inne');
                if (index >= 0) {
                    list[index] = other;
                }
            }
            if (typeof other === 'object') {
                list = list.filter(el => {
                    const listElementsToExclude = ['Inne', 'Produkty dla niemowląt', 'Artykuły dziecięce', 'Płyny czyszczące'];
                    return !listElementsToExclude.includes(el);
                });
                list = list.concat(Array.from(other));
            }
            return list;
        },
        getCustom (index, custom) {
            if (this.formattedNeeds?.[index]) {
                const items = [];
                for (const item in custom) {
                    if (this.formattedNeeds[index]?.[item]) {
                        items.push(this.formattedNeeds[index][item]);
                    }
                }
                return items.length > 0 ? items.join(', ') : null;
            }
            return null;
        }
    }
};
</script>

<style scoped></style>
